import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Clock, Send, Settings2, CreditCard, Banknote, Receipt, Building2, Monitor, BarChart3, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const [generalSettings, setGeneralSettings] = useState({
    vat_rate: 20,
    business_name: 'Kasseport Café',
    business_address: '',
    vat_number: '',
    feedback_url: '',
    payment_methods: {
      card_enabled: true,
      cash_enabled: true,
      account_enabled: true,
      default_method: 'card',
      require_receipt_confirmation: true
    },
    breakfast_start: '07:00',
    breakfast_end: '10:00',
    lunch_start: '12:00',
    lunch_end: '14:00'
  });
  const [emailSettings, setEmailSettings] = useState({
    admin_email: '',
    from_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    send_daily_summary: false,
    summary_time: '18:00'
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [multiTill, setMultiTill] = useState({ enabled: false, terminals: [] });
  const [autoReports, setAutoReports] = useState({
    enabled: false, frequency: 'daily', send_time: '18:00', recipients: [],
    include_revenue: true, include_best_sellers: true, include_stock_alerts: true, include_operator_breakdown: false
  });
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalLocation, setNewTerminalLocation] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [savingTill, setSavingTill] = useState(false);
  const [savingReports, setSavingReports] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [generalRes, emailRes, tillRes, reportsRes] = await Promise.all([
        axios.get(`${API}/settings/general`),
        axios.get(`${API}/settings/email`),
        axios.get(`${API}/settings/multi-till`).catch(() => ({ data: { enabled: false, terminals: [] } })),
        axios.get(`${API}/settings/auto-reports`).catch(() => ({ data: { enabled: false, frequency: 'daily', send_time: '18:00', recipients: [] } }))
      ]);
      
      if (generalRes.data) {
        setGeneralSettings({
          ...generalSettings,
          ...generalRes.data,
          business_address: generalRes.data.business_address || '',
          vat_number: generalRes.data.vat_number || '',
          feedback_url: generalRes.data.feedback_url || '',
          payment_methods: {
            ...generalSettings.payment_methods,
            ...generalRes.data.payment_methods
          }
        });
      }
      
      if (emailRes.data) {
        setEmailSettings({
          admin_email: emailRes.data.admin_email || '',
          from_email: emailRes.data.from_email || '',
          smtp_host: emailRes.data.smtp_host || '',
          smtp_port: emailRes.data.smtp_port || 587,
          smtp_username: emailRes.data.smtp_username || '',
          smtp_password: '',
          smtp_use_tls: true,
          send_daily_summary: emailRes.data.send_daily_summary,
          summary_time: emailRes.data.summary_time
        });
        setSmtpConfigured(emailRes.data.smtp_configured || false);
      }

      if (tillRes.data) setMultiTill(tillRes.data);
      if (reportsRes.data) setAutoReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      await axios.post(`${API}/settings/general`, generalSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!emailSettings.admin_email) {
      toast.error('Please enter an admin email address');
      return;
    }
    setSavingEmail(true);
    try {
      await axios.post(`${API}/settings/email`, emailSettings);
      toast.success('Email settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSendSummary = async () => {
    if (!emailSettings.admin_email) {
      toast.error('Please configure admin email first');
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(`${API}/email/send-daily-summary`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send summary');
    } finally {
      setSending(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingSMTP(true);
    try {
      const response = await axios.post(`${API}/email/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTestingSMTP(false);
    }
  };

  const updatePaymentMethod = (key, value) => {
    setGeneralSettings({
      ...generalSettings,
      payment_methods: {
        ...generalSettings.payment_methods,
        [key]: value
      }
    });
  };

  const handleSaveMultiTill = async () => {
    setSavingTill(true);
    try {
      const res = await axios.post(`${API}/settings/multi-till`, multiTill);
      setMultiTill(res.data);
      toast.success('Multi-till settings saved');
    } catch (error) { toast.error('Failed to save'); }
    finally { setSavingTill(false); }
  };

  const addTerminal = () => {
    if (!newTerminalName.trim()) { toast.error('Terminal name is required'); return; }
    setMultiTill(prev => ({
      ...prev,
      terminals: [...prev.terminals, { name: newTerminalName.trim(), location: newTerminalLocation.trim(), active: true }]
    }));
    setNewTerminalName('');
    setNewTerminalLocation('');
  };

  const removeTerminal = (idx) => {
    setMultiTill(prev => ({
      ...prev,
      terminals: prev.terminals.filter((_, i) => i !== idx)
    }));
  };

  const handleSaveAutoReports = async () => {
    setSavingReports(true);
    try {
      await axios.post(`${API}/settings/auto-reports`, autoReports);
      toast.success('Automated reports settings saved');
    } catch (error) { toast.error('Failed to save'); }
    finally { setSavingReports(false); }
  };

  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (autoReports.recipients.includes(email)) { toast.error('Already added'); return; }
    setAutoReports(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
    setNewRecipient('');
  };

  const removeRecipient = (idx) => {
    setAutoReports(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== idx)
    }));
  };

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
    timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1 font-body">Configure system preferences</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">
            <Settings2 className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="w-4 h-4 mr-2" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="multi-till" data-testid="tab-multi-till">
            <Monitor className="w-4 h-4 mr-2" />
            Multi-Till
          </TabsTrigger>
          <TabsTrigger value="auto-reports" data-testid="tab-auto-reports">
            <BarChart3 className="w-4 h-4 mr-2" />
            Auto Reports
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Business Info */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Business Information
                </CardTitle>
                <CardDescription>Details shown on receipts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    data-testid="business-name-input"
                    value={generalSettings.business_name}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, business_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address">Business Address</Label>
                  <Input
                    id="business_address"
                    data-testid="business-address-input"
                    value={generalSettings.business_address}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, business_address: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    data-testid="vat-number-input"
                    value={generalSettings.vat_number}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, vat_number: e.target.value })}
                    placeholder="e.g., GB123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feedback_url">Feedback URL</Label>
                  <Input
                    id="feedback_url"
                    data-testid="feedback-url-input"
                    value={generalSettings.feedback_url || ''}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, feedback_url: e.target.value })}
                    placeholder="https://forms.gle/..."
                  />
                  <p className="text-xs text-slate-500">QR code linking to this URL will appear on receipts</p>
                </div>
              </CardContent>
            </Card>

            {/* VAT Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  VAT Settings
                </CardTitle>
                <CardDescription>Configure tax rate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vat_rate">VAT Rate (%)</Label>
                  <Input
                    id="vat_rate"
                    data-testid="vat-rate-input"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={generalSettings.vat_rate}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, vat_rate: parseFloat(e.target.value) })}
                  />
                  <p className="text-xs text-slate-500">
                    Current UK standard rate is 20%
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Time-based Categories */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Service Time Slots
                </CardTitle>
                <CardDescription>Auto-select categories based on time of day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-3">Breakfast Service</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Start Time</Label>
                        <Select
                          value={generalSettings.breakfast_start}
                          onValueChange={(v) => setGeneralSettings({ ...generalSettings, breakfast_start: v })}
                        >
                          <SelectTrigger data-testid="breakfast-start-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">End Time</Label>
                        <Select
                          value={generalSettings.breakfast_end}
                          onValueChange={(v) => setGeneralSettings({ ...generalSettings, breakfast_end: v })}
                        >
                          <SelectTrigger data-testid="breakfast-end-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-3">Lunch Service</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Start Time</Label>
                        <Select
                          value={generalSettings.lunch_start}
                          onValueChange={(v) => setGeneralSettings({ ...generalSettings, lunch_start: v })}
                        >
                          <SelectTrigger data-testid="lunch-start-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">End Time</Label>
                        <Select
                          value={generalSettings.lunch_end}
                          onValueChange={(v) => setGeneralSettings({ ...generalSettings, lunch_end: v })}
                        >
                          <SelectTrigger data-testid="lunch-end-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Button onClick={handleSaveGeneral} disabled={savingGeneral} data-testid="save-general-btn">
            {savingGeneral ? 'Saving...' : 'Save General Settings'}
          </Button>
        </TabsContent>

        {/* Payment Settings */}
        <TabsContent value="payments" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl">Payment Methods</CardTitle>
              <CardDescription>Configure available payment options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Card/Contactless</p>
                      <p className="text-xs text-slate-500">External PDQ</p>
                    </div>
                  </div>
                  <Switch
                    checked={generalSettings.payment_methods.card_enabled}
                    onCheckedChange={(v) => updatePaymentMethod('card_enabled', v)}
                    data-testid="card-enabled-switch"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">Cash</p>
                      <p className="text-xs text-slate-500">Manual handling</p>
                    </div>
                  </div>
                  <Switch
                    checked={generalSettings.payment_methods.cash_enabled}
                    onCheckedChange={(v) => updatePaymentMethod('cash_enabled', v)}
                    data-testid="cash-enabled-switch"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Receipt className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="font-medium">Account</p>
                      <p className="text-xs text-slate-500">Requires signature</p>
                    </div>
                  </div>
                  <Switch
                    checked={generalSettings.payment_methods.account_enabled}
                    onCheckedChange={(v) => updatePaymentMethod('account_enabled', v)}
                    data-testid="account-enabled-switch"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Payment Method</Label>
                  <Select
                    value={generalSettings.payment_methods.default_method}
                    onValueChange={(v) => updatePaymentMethod('default_method', v)}
                  >
                    <SelectTrigger data-testid="default-payment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">Card/Contactless</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="account">Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Receipt Confirmation</p>
                    <p className="text-xs text-slate-500">Ask before printing receipt</p>
                  </div>
                  <Switch
                    checked={generalSettings.payment_methods.require_receipt_confirmation}
                    onCheckedChange={(v) => updatePaymentMethod('require_receipt_confirmation', v)}
                    data-testid="receipt-confirm-switch"
                  />
                </div>
              </div>

              <Button onClick={handleSaveGeneral} disabled={savingGeneral} data-testid="save-payments-btn">
                {savingGeneral ? 'Saving...' : 'Save Payment Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Configuration
              </CardTitle>
              <CardDescription>Configure SMTP settings for receipts and daily summaries</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMTP Status */}
              <div className={`p-3 rounded-lg text-sm ${smtpConfigured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {smtpConfigured ? 'SMTP configured and ready to send emails' : 'SMTP not configured — emails will be prepared but not sent'}
              </div>

              {/* Email addresses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Admin Email (receives summaries)</Label>
                  <Input data-testid="admin-email-input" type="email" placeholder="admin@example.com"
                    value={emailSettings.admin_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, admin_email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>From Email (sender address)</Label>
                  <Input data-testid="from-email-input" type="email" placeholder="noreply@yourdomain.com"
                    value={emailSettings.from_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })} />
                </div>
              </div>

              {/* SMTP Settings */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-4">
                <h3 className="font-medium text-slate-700 text-sm">SMTP Server</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>SMTP Host</Label>
                    <Input data-testid="smtp-host-input" placeholder="smtp.gmail.com"
                      value={emailSettings.smtp_host}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input data-testid="smtp-port-input" type="number" placeholder="587"
                      value={emailSettings.smtp_port}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input data-testid="smtp-user-input" placeholder="your@email.com"
                      value={emailSettings.smtp_username}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input data-testid="smtp-pass-input" type="password" placeholder="Leave blank to keep existing"
                      value={emailSettings.smtp_password}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="smtp-tls-toggle" checked={emailSettings.smtp_use_tls}
                    onCheckedChange={(v) => setEmailSettings({ ...emailSettings, smtp_use_tls: v })} />
                  <Label>Use TLS (recommended for port 587)</Label>
                </div>
                <p className="text-xs text-slate-400">
                  Common providers: Gmail (smtp.gmail.com:587), Brevo (smtp-relay.brevo.com:587), Outlook (smtp-mail.outlook.com:587), Mailjet (in-v3.mailjet.com:587)
                </p>
              </div>

              {/* Daily Summary */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Daily Sales Summary</Label>
                  <p className="text-sm text-slate-500">Receive daily revenue and stock reports</p>
                </div>
                <Switch checked={emailSettings.send_daily_summary}
                  onCheckedChange={(v) => setEmailSettings({ ...emailSettings, send_daily_summary: v })}
                  data-testid="daily-summary-toggle" />
              </div>

              {emailSettings.send_daily_summary && (
                <div className="space-y-2">
                  <Label>Summary Time</Label>
                  <Select value={emailSettings.summary_time}
                    onValueChange={(v) => setEmailSettings({ ...emailSettings, summary_time: v })}>
                    <SelectTrigger className="w-32" data-testid="summary-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                <Button onClick={handleSaveEmail} disabled={savingEmail} data-testid="save-email-settings-btn">
                  {savingEmail ? 'Saving...' : 'Save Email Settings'}
                </Button>
                <Button variant="outline" onClick={handleTestEmail} disabled={testingSMTP || !emailSettings.smtp_host}
                  data-testid="test-email-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  {testingSMTP ? 'Sending...' : 'Send Test Email'}
                </Button>
                <Button variant="outline" onClick={handleSendSummary}
                  disabled={sending || !emailSettings.admin_email}
                  data-testid="send-summary-btn">
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending...' : 'Send Daily Summary'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Multi-Till Settings */}
        <TabsContent value="multi-till" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Multi-Till Support
              </CardTitle>
              <CardDescription>Configure multiple till terminals for different service points</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-3 rounded-lg text-sm bg-slate-50 text-slate-600">
                This feature is not yet active. Configure your till terminals here for when multi-till support is enabled.
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Enable Multi-Till</Label>
                  <p className="text-sm text-slate-500">Allow multiple named till terminals with separate reporting</p>
                </div>
                <Switch
                  data-testid="multi-till-enabled"
                  checked={multiTill.enabled}
                  onCheckedChange={(v) => setMultiTill(prev => ({ ...prev, enabled: v }))}
                />
              </div>

              <div className="space-y-3">
                <Label>Till Terminals</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="new-terminal-name"
                    placeholder="Terminal name (e.g. Till 1)"
                    value={newTerminalName}
                    onChange={(e) => setNewTerminalName(e.target.value)}
                  />
                  <Input
                    data-testid="new-terminal-location"
                    placeholder="Location (optional)"
                    value={newTerminalLocation}
                    onChange={(e) => setNewTerminalLocation(e.target.value)}
                    className="w-48"
                  />
                  <Button variant="outline" onClick={addTerminal} data-testid="add-terminal-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {multiTill.terminals.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">No terminals configured yet</p>
                ) : (
                  <div className="space-y-2">
                    {multiTill.terminals.map((t, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-medium text-slate-800">{t.name}</span>
                          {t.location && <span className="text-sm text-slate-500 ml-2">({t.location})</span>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeTerminal(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={handleSaveMultiTill} disabled={savingTill} data-testid="save-multi-till-btn">
                {savingTill ? 'Saving...' : 'Save Multi-Till Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automated Reports Settings */}
        <TabsContent value="auto-reports" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Automated Reports
              </CardTitle>
              <CardDescription>Schedule automatic email reports for sales and stock data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-3 rounded-lg text-sm bg-slate-50 text-slate-600">
                This feature is not yet active. Configure your report preferences here for when automated scheduling is enabled.
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Enable Automated Reports</Label>
                  <p className="text-sm text-slate-500">Automatically send reports by email on a schedule</p>
                </div>
                <Switch
                  data-testid="auto-reports-enabled"
                  checked={autoReports.enabled}
                  onCheckedChange={(v) => setAutoReports(prev => ({ ...prev, enabled: v }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={autoReports.frequency}
                    onValueChange={(v) => setAutoReports(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger data-testid="report-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly (Monday)</SelectItem>
                      <SelectItem value="monthly">Monthly (1st)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Send Time</Label>
                  <Select
                    value={autoReports.send_time}
                    onValueChange={(v) => setAutoReports(prev => ({ ...prev, send_time: v }))}
                  >
                    <SelectTrigger data-testid="report-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Report Contents</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm text-slate-700">Revenue Summary</span>
                    <Switch checked={autoReports.include_revenue}
                      onCheckedChange={(v) => setAutoReports(prev => ({ ...prev, include_revenue: v }))}
                      data-testid="report-include-revenue" />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm text-slate-700">Best Sellers</span>
                    <Switch checked={autoReports.include_best_sellers}
                      onCheckedChange={(v) => setAutoReports(prev => ({ ...prev, include_best_sellers: v }))}
                      data-testid="report-include-bestsellers" />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm text-slate-700">Stock Alerts</span>
                    <Switch checked={autoReports.include_stock_alerts}
                      onCheckedChange={(v) => setAutoReports(prev => ({ ...prev, include_stock_alerts: v }))}
                      data-testid="report-include-stock" />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm text-slate-700">Operator Breakdown</span>
                    <Switch checked={autoReports.include_operator_breakdown}
                      onCheckedChange={(v) => setAutoReports(prev => ({ ...prev, include_operator_breakdown: v }))}
                      data-testid="report-include-operator" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="new-recipient-email"
                    type="email"
                    placeholder="email@example.com"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  />
                  <Button variant="outline" onClick={addRecipient} data-testid="add-recipient-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {autoReports.recipients.length === 0 ? (
                  <p className="text-sm text-slate-400 py-2">No recipients added yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {autoReports.recipients.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 rounded-full text-sm">
                        <span className="text-slate-700">{email}</span>
                        <button onClick={() => removeRecipient(idx)} className="text-slate-400 hover:text-red-500 ml-1">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={handleSaveAutoReports} disabled={savingReports} data-testid="save-auto-reports-btn">
                {savingReports ? 'Saving...' : 'Save Report Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
